const DATABASE_NAME = "browser-local-backend-artifacts";
const DATABASE_VERSION = 2;
const STORE_NAME = "vault-artifacts";
const TRANSFER_STORE_NAME = "vault-transfer-sessions";
export const MAX_VAULT_ARTIFACT_BYTES = 128 * 1024 * 1024;

export type VaultArtifact = {
  id: string;
  name: string;
  size: number;
  lastModified: number;
  sha256: string;
  importedAt: string;
  lastVerifiedAt?: string;
  lastVerificationOk?: boolean;
};

export type VaultImportResult = {
  artifact: VaultArtifact;
  duplicate: boolean;
  nameConflict?: Pick<VaultArtifact, "id" | "name" | "sha256">;
};

export type VaultVerificationResult = {
  artifact: VaultArtifact;
  ok: boolean;
  expectedSha256: string;
  actualSha256: string;
};

export type OpaqueTransferMetadata = {
  name: string;
  size: number;
  sha256: string;
};

export type PendingVaultTransfer = OpaqueTransferMetadata & {
  receivedBytes: number;
  chunks: Blob[];
  startedAt: string;
  updatedAt: string;
};

type StoredVaultArtifact = VaultArtifact & { blob: Blob };

export class VaultArtifactError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = "VaultArtifactError";
  }
}

export class VaultArtifactStore {
  private constructor(private readonly database: IDBDatabase) {}

  static async open(): Promise<VaultArtifactStore> {
    if (typeof indexedDB === "undefined") {
      throw new VaultArtifactError("indexeddb_unavailable", "IndexedDB is unavailable in this browser.");
    }

    const database = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
        if (!request.result.objectStoreNames.contains(TRANSFER_STORE_NAME)) {
          request.result.createObjectStore(TRANSFER_STORE_NAME, { keyPath: "sha256" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Could not open artifact storage."));
    });

    return new VaultArtifactStore(database);
  }

  async importFile(file: File): Promise<VaultImportResult> {
    if (!file.name.toLowerCase().endsWith(".vault")) {
      throw new VaultArtifactError("invalid_extension", "Only .vault files can be stored as Vortex artifacts.");
    }
    if (file.size > MAX_VAULT_ARTIFACT_BYTES) {
      throw new VaultArtifactError("artifact_too_large", "This offline artifact locker is limited to 128 MiB. Open larger vaults directly in Vortex.");
    }

    const bytes = new Uint8Array(await file.arrayBuffer());
    const sha256 = await sha256Hex(bytes);
    const existing = await this.findBySha256(sha256);
    if (existing) {
      return { artifact: existing, duplicate: true };
    }
    const nameConflict = await this.findByName(file.name);
    const artifact: StoredVaultArtifact = {
      id: crypto.randomUUID(),
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
      sha256,
      importedAt: new Date().toISOString(),
      blob: new Blob([bytes], { type: file.type || "application/octet-stream" }),
    };
    await this.put(artifact);
    return { artifact: metadataOf(artifact), duplicate: false, nameConflict };
  }

  async list(): Promise<VaultArtifact[]> {
    const values = await this.transaction<StoredVaultArtifact[]>("readonly", (store) => requestToPromise(store.getAll()));
    return values
      .map(metadataOf)
      .sort((a, b) => b.importedAt.localeCompare(a.importedAt));
  }

  async hasSha256(sha256: string): Promise<boolean> {
    return Boolean(await this.findBySha256(sha256));
  }

  async getPendingTransfer(sha256: string): Promise<PendingVaultTransfer | undefined> {
    return this.transferTransaction<PendingVaultTransfer | undefined>("readonly", (store) => requestToPromise(store.get(sha256)));
  }

  async appendTransferChunk(metadata: OpaqueTransferMetadata, bytes: ArrayBuffer): Promise<number> {
    if (metadata.size > MAX_VAULT_ARTIFACT_BYTES || metadata.size < 0) {
      throw new VaultArtifactError("transfer_too_large", "Incoming transfer exceeds the opaque artifact limit.");
    }
    const current = await this.getPendingTransfer(metadata.sha256);
    if (current && (current.name !== metadata.name || current.size !== metadata.size)) {
      throw new VaultArtifactError("transfer_conflict", "A pending transfer with the same hash has different metadata.");
    }
    const receivedBytes = current?.receivedBytes ?? 0;
    if (receivedBytes + bytes.byteLength > metadata.size) {
      throw new VaultArtifactError("transfer_overflow", "Incoming transfer exceeded its declared size.");
    }
    const buffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buffer).set(new Uint8Array(bytes));
    const now = new Date().toISOString();
    const next: PendingVaultTransfer = {
      name: metadata.name,
      size: metadata.size,
      sha256: metadata.sha256,
      receivedBytes: receivedBytes + bytes.byteLength,
      chunks: [...(current?.chunks ?? []), new Blob([buffer], { type: "application/octet-stream" })],
      startedAt: current?.startedAt ?? now,
      updatedAt: now,
    };
    await this.transferTransaction("readwrite", (store) => requestToPromise(store.put(next)));
    return next.receivedBytes;
  }

  async clearPendingTransfer(sha256: string): Promise<void> {
    await this.transferTransaction("readwrite", (store) => requestToPromise(store.delete(sha256)));
  }

  async getBlob(id: string): Promise<{ metadata: VaultArtifact; blob: Blob } | undefined> {
    const value = await this.transaction<StoredVaultArtifact | undefined>("readonly", (store) => requestToPromise(store.get(id)));
    return value ? { metadata: metadataOf(value), blob: value.blob } : undefined;
  }

  async verify(id: string): Promise<VaultVerificationResult> {
    const value = await this.transaction<StoredVaultArtifact | undefined>("readonly", (store) => requestToPromise(store.get(id)));
    if (!value) throw new VaultArtifactError("artifact_not_found", "The opaque artifact no longer exists.");

    const actualSha256 = await sha256Hex(new Uint8Array(await value.blob.arrayBuffer()));
    const ok = actualSha256 === value.sha256;
    const verified: StoredVaultArtifact = {
      ...value,
      lastVerifiedAt: new Date().toISOString(),
      lastVerificationOk: ok,
    };
    await this.put(verified);
    return {
      artifact: metadataOf(verified),
      ok,
      expectedSha256: value.sha256,
      actualSha256,
    };
  }

  async remove(id: string): Promise<void> {
    await this.transaction("readwrite", (store) => requestToPromise(store.delete(id)));
  }

  close(): void {
    this.database.close();
  }

  private transaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => Promise<T>): Promise<T> {
    const transaction = this.database.transaction(STORE_NAME, mode);
    return new Promise<T>((resolve, reject) => {
      let result: T;
      let operationFinished = false;
      transaction.oncomplete = () => {
        if (operationFinished) resolve(result);
      };
      transaction.onerror = () => reject(transaction.error ?? new Error("Artifact transaction failed."));
      transaction.onabort = () => reject(transaction.error ?? new Error("Artifact transaction aborted."));
      void operation(transaction.objectStore(STORE_NAME))
        .then((value) => {
          result = value;
          operationFinished = true;
        })
        .catch(reject);
    });
  }

  private transferTransaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => Promise<T>): Promise<T> {
    const transaction = this.database.transaction(TRANSFER_STORE_NAME, mode);
    return new Promise<T>((resolve, reject) => {
      let result: T;
      let operationFinished = false;
      transaction.oncomplete = () => {
        if (operationFinished) resolve(result);
      };
      transaction.onerror = () => reject(transaction.error ?? new Error("Transfer session transaction failed."));
      transaction.onabort = () => reject(transaction.error ?? new Error("Transfer session transaction aborted."));
      void operation(transaction.objectStore(TRANSFER_STORE_NAME))
        .then((value) => {
          result = value;
          operationFinished = true;
        })
        .catch(reject);
    });
  }

  private async put(value: StoredVaultArtifact): Promise<void> {
    await this.transaction("readwrite", (store) => requestToPromise(store.put(value)));
  }

  private async findBySha256(sha256: string): Promise<VaultArtifact | undefined> {
    const values = await this.transaction<StoredVaultArtifact[]>("readonly", (store) => requestToPromise(store.getAll()));
    const existing = values.find((value) => value.sha256 === sha256);
    return existing ? metadataOf(existing) : undefined;
  }

  private async findByName(name: string): Promise<Pick<VaultArtifact, "id" | "name" | "sha256"> | undefined> {
    const values = await this.transaction<StoredVaultArtifact[]>("readonly", (store) => requestToPromise(store.getAll()));
    const existing = values.find((value) => value.name === name);
    return existing ? { id: existing.id, name: existing.name, sha256: existing.sha256 } : undefined;
  }
}

function metadataOf(value: StoredVaultArtifact): VaultArtifact {
  const { blob: _blob, ...metadata } = value;
  return metadata;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Artifact storage request failed."));
  });
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
